import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import { useEffect, useImperativeHandle, forwardRef, useRef, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Heading3,
  Quote,
  Undo,
  Redo,
  Pilcrow,
  Link as LinkIcon,
  Unlink,
  Image as ImageIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";

export interface RichTextEditorHandle {
  insertText: (text: string) => void;
  getEditor: () => Editor | null;
}

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minHeight?: string;
}

function plainTextToHtml(input: string): string {
  if (!input) return "";
  if (/<\/?[a-z][^>]*>/i.test(input)) return input;
  return input
    .split(/\n{2,}/)
    .map((para) => `<p>${para.replace(/\n/g, "<br>")}</p>`)
    .join("");
}

function isSafeLinkUrl(url: string): boolean {
  const trimmed = url.trim();
  if (!trimmed) return false;
  return /^(https?:|mailto:|tel:|\/|#)/i.test(trimmed);
}

const ALLOWED_IMAGE_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/svg+xml",
]);
const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10 MB

async function uploadImageFile(file: File): Promise<string> {
  if (!ALLOWED_IMAGE_TYPES.has(file.type)) {
    throw new Error("Unsupported image type. Use PNG, JPEG, GIF, WebP, or SVG.");
  }
  if (file.size > MAX_IMAGE_BYTES) {
    throw new Error("Image is too large (10 MB max).");
  }

  const reqRes = await fetch("/api/storage/uploads/request-url", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      name: file.name,
      size: file.size,
      contentType: file.type,
    }),
  });
  if (!reqRes.ok) {
    throw new Error(`Could not start upload (${reqRes.status}).`);
  }
  const { uploadURL, objectPath, requiredHeaders } = (await reqRes.json()) as {
    uploadURL: string;
    objectPath: string;
    requiredHeaders?: Record<string, string>;
  };

  const putRes = await fetch(uploadURL, {
    method: "PUT",
    headers: {
      "Content-Type": requiredHeaders?.["Content-Type"] ?? file.type,
    },
    body: file,
  });
  if (!putRes.ok) {
    throw new Error(`Upload failed (${putRes.status}).`);
  }

  return `/api/storage${objectPath}`;
}

export const RichTextEditor = forwardRef<RichTextEditorHandle, RichTextEditorProps>(
  function RichTextEditor({ value, onChange, placeholder, disabled, className, minHeight = "200px" }, ref) {
    const [uploadingCount, setUploadingCount] = useState(0);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const editorRef = useRef<Editor | null>(null);

    const handleUpload = useCallback(async (file: File) => {
      setUploadError(null);
      setUploadingCount((n) => n + 1);
      try {
        const url = await uploadImageFile(file);
        const ed = editorRef.current;
        if (ed) {
          ed.chain().focus().setImage({ src: url, alt: file.name }).run();
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : "Upload failed.";
        setUploadError(message);
      } finally {
        setUploadingCount((n) => Math.max(0, n - 1));
      }
    }, []);

    const handleFiles = useCallback(
      (files: FileList | File[] | null | undefined): boolean => {
        if (!files) return false;
        const images = Array.from(files).filter((f) => f.type.startsWith("image/"));
        if (images.length === 0) return false;
        for (const file of images) {
          void handleUpload(file);
        }
        return true;
      },
      [handleUpload],
    );

    const editor = useEditor({
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2, 3] },
        }),
        Link.configure({
          openOnClick: false,
          autolink: true,
          linkOnPaste: true,
          HTMLAttributes: {
            rel: "noopener noreferrer nofollow",
            target: "_blank",
            class: "text-primary underline underline-offset-2",
          },
        }),
        Image.configure({
          inline: false,
          allowBase64: false,
          HTMLAttributes: {
            class: "max-w-full h-auto rounded",
          },
        }),
      ],
      content: plainTextToHtml(value),
      editable: !disabled,
      editorProps: {
        attributes: {
          class: cn(
            "prose prose-sm max-w-none dark:prose-invert focus:outline-none px-3 py-2 text-sm leading-relaxed",
          ),
          style: `min-height: ${minHeight};`,
        },
        handleDrop: (_view, event) => {
          if (disabled) return false;
          const dt = (event as DragEvent).dataTransfer;
          if (!dt || dt.files.length === 0) return false;
          if (handleFiles(dt.files)) {
            event.preventDefault();
            return true;
          }
          return false;
        },
        handlePaste: (_view, event) => {
          if (disabled) return false;
          const items = (event as ClipboardEvent).clipboardData?.items;
          if (!items) return false;
          const files: File[] = [];
          for (const item of Array.from(items)) {
            if (item.kind === "file") {
              const file = item.getAsFile();
              if (file && file.type.startsWith("image/")) {
                files.push(file);
              }
            }
          }
          if (files.length === 0) return false;
          if (handleFiles(files)) {
            event.preventDefault();
            return true;
          }
          return false;
        },
      },
      onUpdate: ({ editor: ed }) => {
        const html = ed.getHTML();
        onChange(html === "<p></p>" ? "" : html);
      },
    });

    useEffect(() => {
      editorRef.current = editor;
    }, [editor]);

    useEffect(() => {
      if (!editor) return;
      const current = editor.getHTML();
      const incoming = plainTextToHtml(value) || "<p></p>";
      if (current !== incoming && (current === "<p></p>" || !editor.isFocused)) {
        editor.commands.setContent(incoming, { emitUpdate: false });
      }
    }, [value, editor]);

    useEffect(() => {
      if (!editor) return;
      editor.setEditable(!disabled);
    }, [editor, disabled]);

    useImperativeHandle(
      ref,
      () => ({
        insertText: (text: string) => {
          if (!editor) return;
          editor.chain().focus().insertContent(text).run();
        },
        getEditor: () => editor,
      }),
      [editor],
    );

    if (!editor) {
      return (
        <div
          className={cn("rounded-md border bg-muted/20 animate-pulse", className)}
          style={{ minHeight }}
        />
      );
    }

    return (
      <div className={cn("rounded-md border bg-background", className)}>
        <Toolbar
          editor={editor}
          disabled={disabled}
          onUploadFile={handleUpload}
          uploading={uploadingCount > 0}
        />
        <div className="border-t">
          <EditorContent editor={editor} placeholder={placeholder} />
        </div>
        {uploadingCount > 0 && (
          <div
            role="status"
            aria-live="polite"
            className="border-t px-3 py-1.5 text-xs text-muted-foreground bg-muted/30"
          >
            Uploading image…
          </div>
        )}
        {uploadError && (
          <div
            role="alert"
            className="border-t px-3 py-1.5 text-xs text-destructive bg-destructive/5 flex items-center justify-between gap-2"
          >
            <span>{uploadError}</span>
            <button
              type="button"
              className="underline underline-offset-2"
              onClick={() => setUploadError(null)}
            >
              Dismiss
            </button>
          </div>
        )}
      </div>
    );
  },
);

interface ToolbarProps {
  editor: Editor;
  disabled?: boolean;
  onUploadFile: (file: File) => void;
  uploading: boolean;
}

interface LinkDialogState {
  open: boolean;
  url: string;
  text: string;
  originalText: string;
  from: number;
  to: number;
  hadSelection: boolean;
  isEditing: boolean;
}

function Toolbar({ editor, disabled, onUploadFile, uploading }: ToolbarProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [linkDialog, setLinkDialog] = useState<LinkDialogState>({
    open: false,
    url: "",
    text: "",
    originalText: "",
    from: 0,
    to: 0,
    hadSelection: false,
    isEditing: false,
  });
  const [linkError, setLinkError] = useState<{ url?: string; text?: string }>({});

  const Btn = ({
    onClick,
    active,
    label,
    children,
    canDo = true,
  }: {
    onClick: () => void;
    active?: boolean;
    label: string;
    children: React.ReactNode;
    canDo?: boolean;
  }) => (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      onClick={onClick}
      disabled={disabled || !canDo}
      aria-label={label}
      title={label}
      data-active={active ? "true" : undefined}
      className={cn(
        "h-7 w-7 p-0",
        active && "bg-muted text-foreground",
      )}
    >
      {children}
    </Button>
  );

  const openLinkDialog = () => {
    const isEditing = editor.isActive("link");
    let from = editor.state.selection.from;
    let to = editor.state.selection.to;

    if (isEditing) {
      // Expand to the full link range so we capture the linked text,
      // even when the selection is just a caret inside the link.
      const { $from } = editor.state.selection;
      const linkMark = $from.marks().find((m) => m.type.name === "link");
      if (linkMark) {
        let start = $from.pos;
        let end = $from.pos;
        editor.state.doc.nodesBetween(0, editor.state.doc.content.size, (node, pos) => {
          if (node.isText && node.marks.some((m) => m.eq(linkMark))) {
            const nodeFrom = pos;
            const nodeTo = pos + node.nodeSize;
            if (nodeFrom <= $from.pos && nodeTo >= $from.pos) {
              start = nodeFrom;
              end = nodeTo;
            }
          }
        });
        from = start;
        to = end;
      }
    }

    const selectedText = editor.state.doc.textBetween(from, to, " ");
    const previousHref = (editor.getAttributes("link").href as string | undefined) ?? "";
    const hasRange = from !== to;

    setLinkError({});
    setLinkDialog({
      open: true,
      url: previousHref || "",
      text: selectedText,
      originalText: selectedText,
      from,
      to,
      // When editing an existing link, treat the captured link range as the
      // selection so the text field is optional (it pre-fills the link text).
      hadSelection: hasRange || isEditing,
      isEditing,
    });
  };

  const submitLinkDialog = () => {
    const url = linkDialog.url.trim();
    const text = linkDialog.text.trim();
    const errors: { url?: string; text?: string } = {};

    if (!url) {
      errors.url = "Please enter a URL.";
    } else if (!isSafeLinkUrl(url)) {
      errors.url = "Use http(s)://, mailto:, tel:, or a relative link starting with / or #.";
    }

    if (!linkDialog.hadSelection && !text) {
      errors.text = "Please enter the text to display for this link.";
    }

    if (errors.url || errors.text) {
      setLinkError(errors);
      return;
    }

    const { from, to, originalText } = linkDialog;
    const hasStoredRange = from !== to;
    // Honor the placeholder hint ("Leave unchanged to keep selected text"):
    // an empty text field in edit mode means "keep existing text".
    const textChanged = text !== "" && text !== originalText;

    if (!hasStoredRange) {
      // No selection (and not editing an existing link): insert new linked text at the caret.
      editor
        .chain()
        .focus()
        .insertContentAt(from, {
          type: "text",
          text: text || url,
          marks: [{ type: "link", attrs: { href: url } }],
        })
        .run();
    } else if (textChanged) {
      // Real range and the user changed the text: replace the range's text and re-apply the link.
      editor
        .chain()
        .focus()
        .insertContentAt({ from, to }, {
          type: "text",
          text,
          marks: [{ type: "link", attrs: { href: url } }],
        })
        .run();
    } else {
      // Keep existing text, just (re)apply the link with the new href across the stored range.
      editor
        .chain()
        .focus()
        .setTextSelection({ from, to })
        .setLink({ href: url })
        .run();
    }

    setLinkDialog((s) => ({ ...s, open: false }));
  };

  const removeLinkFromDialog = () => {
    const { from, to } = linkDialog;
    const chain = editor.chain().focus();
    if (from !== to) {
      chain.setTextSelection({ from, to });
    }
    chain.extendMarkRange("link").unsetLink().run();
    setLinkDialog((s) => ({ ...s, open: false }));
  };

  const handleUnsetLink = () => {
    editor.chain().focus().extendMarkRange("link").unsetLink().run();
  };

  const handleInsertImage = () => {
    fileInputRef.current?.click();
  };

  const handleFileInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      for (const file of Array.from(files)) {
        if (file.type.startsWith("image/")) {
          onUploadFile(file);
        }
      }
    }
    event.target.value = "";
  };

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-1.5 py-1">
      <Btn
        onClick={() => editor.chain().focus().toggleBold().run()}
        active={editor.isActive("bold")}
        label="Bold"
      >
        <Bold className="h-3.5 w-3.5" />
      </Btn>
      <Btn
        onClick={() => editor.chain().focus().toggleItalic().run()}
        active={editor.isActive("italic")}
        label="Italic"
      >
        <Italic className="h-3.5 w-3.5" />
      </Btn>
      <div className="w-px h-5 bg-border mx-1" />
      <Btn
        onClick={() => editor.chain().focus().setParagraph().run()}
        active={editor.isActive("paragraph")}
        label="Paragraph"
      >
        <Pilcrow className="h-3.5 w-3.5" />
      </Btn>
      <Btn
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
        active={editor.isActive("heading", { level: 1 })}
        label="Heading 1"
      >
        <Heading1 className="h-3.5 w-3.5" />
      </Btn>
      <Btn
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        active={editor.isActive("heading", { level: 2 })}
        label="Heading 2"
      >
        <Heading2 className="h-3.5 w-3.5" />
      </Btn>
      <Btn
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        active={editor.isActive("heading", { level: 3 })}
        label="Heading 3"
      >
        <Heading3 className="h-3.5 w-3.5" />
      </Btn>
      <div className="w-px h-5 bg-border mx-1" />
      <Btn
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        active={editor.isActive("bulletList")}
        label="Bullet list"
      >
        <List className="h-3.5 w-3.5" />
      </Btn>
      <Btn
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        active={editor.isActive("orderedList")}
        label="Numbered list"
      >
        <ListOrdered className="h-3.5 w-3.5" />
      </Btn>
      <Btn
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        active={editor.isActive("blockquote")}
        label="Blockquote"
      >
        <Quote className="h-3.5 w-3.5" />
      </Btn>
      <div className="w-px h-5 bg-border mx-1" />
      <Btn
        onClick={openLinkDialog}
        active={editor.isActive("link")}
        label={editor.isActive("link") ? "Edit link" : "Add link"}
      >
        <LinkIcon className="h-3.5 w-3.5" />
      </Btn>
      <Btn
        onClick={handleUnsetLink}
        canDo={editor.isActive("link")}
        label="Remove link"
      >
        <Unlink className="h-3.5 w-3.5" />
      </Btn>
      <Btn
        onClick={handleInsertImage}
        label={uploading ? "Uploading image…" : "Upload image"}
        canDo={!uploading}
      >
        <ImageIcon className="h-3.5 w-3.5" />
      </Btn>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
        className="sr-only"
        onChange={handleFileInputChange}
        aria-hidden="true"
        tabIndex={-1}
      />
      <div className="w-px h-5 bg-border mx-1" />
      <Btn
        onClick={() => editor.chain().focus().undo().run()}
        canDo={editor.can().undo()}
        label="Undo"
      >
        <Undo className="h-3.5 w-3.5" />
      </Btn>
      <Btn
        onClick={() => editor.chain().focus().redo().run()}
        canDo={editor.can().redo()}
        label="Redo"
      >
        <Redo className="h-3.5 w-3.5" />
      </Btn>

      <Dialog
        open={linkDialog.open}
        onOpenChange={(open) => setLinkDialog((s) => ({ ...s, open }))}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{linkDialog.isEditing ? "Edit link" : "Add link"}</DialogTitle>
            <DialogDescription>
              Use http(s)://, mailto:, tel:, or a relative path starting with / or #.
            </DialogDescription>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              submitLinkDialog();
            }}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="rte-link-url">URL</Label>
              <Input
                id="rte-link-url"
                autoFocus
                value={linkDialog.url}
                onChange={(e) => setLinkDialog((s) => ({ ...s, url: e.target.value }))}
                placeholder="https://example.com"
                aria-invalid={Boolean(linkError.url)}
                aria-describedby={linkError.url ? "rte-link-url-error" : undefined}
              />
              {linkError.url && (
                <p id="rte-link-url-error" className="text-xs text-destructive">
                  {linkError.url}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rte-link-text">
                Link text {linkDialog.hadSelection && <span className="text-muted-foreground font-normal">(optional)</span>}
              </Label>
              <Input
                id="rte-link-text"
                value={linkDialog.text}
                onChange={(e) => setLinkDialog((s) => ({ ...s, text: e.target.value }))}
                placeholder={linkDialog.hadSelection ? "Leave unchanged to keep selected text" : "Text to display"}
                aria-invalid={Boolean(linkError.text)}
                aria-describedby={linkError.text ? "rte-link-text-error" : undefined}
              />
              {linkError.text && (
                <p id="rte-link-text-error" className="text-xs text-destructive">
                  {linkError.text}
                </p>
              )}
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              {linkDialog.isEditing && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={removeLinkFromDialog}
                  className="mr-auto text-destructive hover:text-destructive"
                >
                  Remove link
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={() => setLinkDialog((s) => ({ ...s, open: false }))}
              >
                Cancel
              </Button>
              <Button type="submit">{linkDialog.isEditing ? "Update link" : "Add link"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
